import { isNil } from 'lodash-es';
import { policyModel } from 'common/api/models';
import { permissionModel, policyModelRaw } from 'common/api/models/website';
import { PermissionsEnum } from 'utils/permissions.enum';

/**
 * @param {policyModel} policies
 * @param {number} policyId
 * @return {policyModel|null}
 */
export const extractPolicy = (policies, policyId) => {
  if (isNil(policies)) return null;

  const policy = policies.find((policy) => policy.id === +policyId);
  return isNil(policy) ? null : policy;
};

/**
 * @param {permissionModel[]} permissions
 * @param {string | PermissionsEnum} permissionName
 * @return {permissionModel|null}
 */
export const extractPermission = (permissions, permissionName) => {
  if (isNil(permissions)) return null;

  const permission = permissions.find((permission) => permission.name === permissionName);
  return isNil(permission) ? null : permission;
};

/**
 * Operates in 3 modes - Add, Remove, Update
 *
 * Add policy to list:
 *  `updatedPolicy` should be `{{
 *        id: -1,
 *        organizationGroup: string,
 *        permissions: permissionModel[]
 *  }}`
 *
 * Remove policy from list:
 *  `updatedPolicy` should be `{{
 *        id: number,
 *        organizationGroup: null,
 *        permissions: null
 *  }}`
 *
 * Update policy in list:
 *  `updatedPolicy` should be `{{
 *        id: number,
 *        organizationGroup: string,
 *        permissions: permissionModel[]|*[]
 *   }}`
 *
 * @param {policyModel[]} policies
 * @param {{id:string, organizationGroup: string, permissions: permissionModel[]}} updatedPolicy
 * @return {policyModel[]}
 */
export const rebuildPolicies = (policies, updatedPolicy) => {
  // add policy to list of current policies
  if (updatedPolicy.id === -1 && updatedPolicy.organizationGroup !== null) {
    return [...policies, { ...updatedPolicy, id: Math.ceil(Math.random() * 100000) }];
  }

  // remove policy
  const { id, organizationGroup, permissions } = updatedPolicy;
  if (id > 0 && organizationGroup === null && permissions === null) {
    return policies.filter((policy) => policy.id !== updatedPolicy.id);
  }

  //find and replace
  return policies.map((policy) => {
    if (policy.id === updatedPolicy.id) {
      return policyModel(updatedPolicy);
    }
    return policy;
  });
};

/**
 * Update policy permissions for provided updated/new permission
 *
 * @param {permissionModel[]} currentPermissions
 * @param {permissionModel} updatedPermission
 * @throws Error if permissions aren't part of policy
 * @return {permissionModel[]|null}
 */
export const rebuildPermissions = (currentPermissions, updatedPermission) => {
  if (currentPermissions.find((permission) => permission.name === updatedPermission.name)) {
    // remove permission from permissions list if there and disabled
    if (
      updatedPermission.name !== PermissionsEnum.CHANNEL_ACCESS ||
      (updatedPermission.name === PermissionsEnum.CHANNEL_ACCESS && updatedPermission.data.length === 0)
    ) {
      return currentPermissions.filter((permission) => permission.name !== updatedPermission.name);
    } else {
      return currentPermissions.map((permission) => {
        if (permission.name === updatedPermission.name) {
          return updatedPermission;
        }
        return permission;
      });
    }
  }

  return [...currentPermissions, permissionModel(updatedPermission)];
};

/**
 * @param {policyModel} policy
 * @param {channelModel} channel
 * @return {boolean}
 */
export const channelIsChecked = (policy, channel) => {
  const permission = policy.permissions.find((permission) => permission.name === PermissionsEnum.CHANNEL_ACCESS);
  if (permission && 'data' in permission && permission.data instanceof Array) {
    return permission.data.indexOf(channel.id) > -1;
  }

  return false;
};

/**
 * In order to full fill requirement to have multiple polices with same name we must add some reference different
 *  then name in order to have possibility to manipulate with dataset effectively.
 *
 * @param {policyModelRaw[]} policies
 * @return {policyModel[]}
 */
export const tokenizePolicies = (policies) => {
  return policies.map((policy) => ({ ...policy, id: Math.ceil(Math.random() * 10000000) }));
};

/**
 * Remove generated tokens
 *
 * @param {policyModel[]} policies
 * @return {policyModelRaw[]}
 */
export const unTokenizePolicies = (policies) => {
  return policies.map(policyModelRaw);
};

/**
 * @param {policyModel[]} polices
 * @return {*}
 */
export const normalizePolices = (polices) => {
  let normalized = {};

  polices.map((policy) =>
    policy.permissions.map((permission) => (normalized[`${permission.name}_${policy.id}`] = permission.data))
  );

  return normalized;
};
